import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  body?: string;
  cta?: ReactNode;
  className?: string;
}

/**
 * Center-stacked empty state — icon, headline, supporting copy, one CTA.
 *
 * Motion (design-system §5, respected via framer-motion's built-in
 * `prefers-reduced-motion` opt-out):
 *   - The icon block scales in on mount then eases into a subtle vertical
 *     float — a slow –3 / +3 px sine so the surface feels alive without
 *     asking for attention.
 *   - Title, body and CTA fade+rise in with a small stagger so the eye
 *     lands on them in the right order.
 */
export function EmptyState({ icon: Icon, title, body, cta, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center gap-3 px-6 py-2",
        className,
      )}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{
          opacity: 1,
          scale: 1,
          // Chain a slow float once the entrance settles. The array-target
          // syntax lets us keep the intro spring separate from the loop.
          y: [0, -3, 0, 3, 0],
        }}
        transition={{
          opacity: { duration: 0.35, ease: "easeOut" },
          scale: { type: "spring", stiffness: 260, damping: 22 },
          y: {
            duration: 3.6,
            repeat: Infinity,
            repeatType: "loop",
            ease: "easeInOut",
            delay: 0.6,
          },
        }}
        className="relative mb-2 flex h-20 w-20 items-center justify-center"
      >
        {/* Soft double: 20%-opacity coral pad behind a solid icon */}
        <Icon
          className="absolute h-20 w-20 text-primary opacity-20"
          strokeWidth={1.25}
          aria-hidden
        />
        <Icon className="relative h-9 w-9 text-ink" strokeWidth={1.75} />
      </motion.div>

      <motion.h3
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.08, ease: "easeOut" }}
        className="font-display text-2xl leading-tight tracking-tight text-ink m-0 text-balance"
        style={{ fontVariationSettings: `"opsz" 48, "wght" 500` }}
      >
        {title}
      </motion.h3>
      {body ? (
        <motion.p
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.16, ease: "easeOut" }}
          className="max-w-[36ch] text-[15px] leading-relaxed text-ink-muted m-0"
        >
          {body}
        </motion.p>
      ) : null}
      {cta ? (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.24, ease: "easeOut" }}
          className="mt-2"
        >
          {cta}
        </motion.div>
      ) : null}
    </div>
  );
}
